/**
 * VideoGrid.tsx — fixed 2x2 grid of up to 4 remote feeds you're watching, plus
 * a small self-preview PIP. Each filled square has local-only controls:
 *   👁 stop watching (unsubscribe)   🔊/🔇 mute/unmute that person's audio
 * These only affect YOUR view — never the publisher or other viewers.
 */
import { useEffect, useRef, useState } from 'react';
import type { ActiveSub, StreamLive, LifetimeStats } from '../hooks/useVidchat.js';

function fmtDuration(sec: number): string {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60), s = sec % 60;
    if (m < 60) return `${m}m${s.toString().padStart(2, '0')}s`;
    const h = Math.floor(m / 60);
    return `${h}h${(m % 60).toString().padStart(2, '0')}m`;
}

/** Build the scrolling-ticker text from a user's lifetime stats. */
function tickerText(name: string, lt: LifetimeStats): string {
    const parts = [
        `🎥 ${fmtDuration(lt.onCamSec)} on camera all-time`,
        `🏆 ${lt.sessions} session${lt.sessions === 1 ? '' : 's'}`,
    ];
    if (lt.petsTotal > 0) parts.push(`🐾 ${lt.petsTotal} pet sighting${lt.petsTotal === 1 ? '' : 's'}`);
    if (lt.maxPersons > 1) parts.push(`👥 peak ${lt.maxPersons}`);
    return `${name} — ` + parts.join('  ·  ');
}

function petBadge(pets: StreamLive['pets']): string {
    const out: string[] = [];
    if (pets.cat) out.push('🐱');
    if (pets.dog) out.push('🐶');
    if (pets.bird) out.push('🐦');
    return out.join('');
}

function RemoteSquare({ sub, live, lifetime, starred, maximized, hidden, attach, onUnwatch, onToggleMute, onToggleMax }: {
    sub: ActiveSub;
    live: StreamLive | undefined;
    lifetime: LifetimeStats | undefined;
    starred: boolean;
    maximized: boolean;
    hidden: boolean;
    attach: (sub: ActiveSub, canvas: HTMLCanvasElement) => void;
    onUnwatch: (streamId: number) => void;
    onToggleMute: (streamId: number) => void;
    onToggleMax: (streamId: number) => void;
}) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    useEffect(() => {
        if (canvasRef.current) attach(sub, canvasRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const away = live?.state === 'away';
    const pets = live ? petBadge(live.pets) : '';

    // mic-only publisher: no video tile, just a "listening" placeholder
    if (sub.audioOnly) {
        return (
            <div className={`vc-sq vc-sq-filled vc-sq-audio ${maximized ? 'vc-sq-max' : ''} ${hidden ? 'vc-sq-hidden' : ''}`}>
                <div className="vc-audio-body">
                    <div className="vc-audio-mic">🎙</div>
                    <div className="vc-audio-name">{sub.name}</div>
                    <div className="vc-audio-tag">audio only</div>
                </div>
                <span className="vc-sq-controls">
                    <button
                        className={`vc-icon-btn ${sub.muted ? 'off' : ''}`}
                        title={sub.muted ? 'unmute' : 'mute'}
                        onClick={() => onToggleMute(sub.streamId)}
                    >{sub.muted ? '🔇' : '🔊'}</button>
                    <button
                        className="vc-icon-btn"
                        title="stop listening"
                        onClick={() => onUnwatch(sub.streamId)}
                    >🎧</button>
                </span>
            </div>
        );
    }

    return (
        <div className={`vc-sq vc-sq-filled ${starred ? 'vc-sq-starred' : ''} ${maximized ? 'vc-sq-max' : ''} ${hidden ? 'vc-sq-hidden' : ''}`}>
            <canvas ref={canvasRef} className="vc-sq-canvas" />

            {/* scavenger-game win: animated gold star */}
            {starred && <div className="vc-star">⭐</div>}

            {/* analytics overlay (client-rendered on top of the video) */}
            {live && (
                <div className="vc-sq-stats">
                    {live.onCamSec > 0 && <span title="time on camera this session">⏱ {fmtDuration(live.onCamSec)}</span>}
                    {live.personCount > 1 && <span className="vc-multi" title="multiple people detected">👥 {live.personCount}</span>}
                    {pets && <span className="vc-pet" title="pet detected!">{pets}</span>}
                </div>
            )}

            {/* away fade: when no person detected for a while, dim to black */}
            <div className={`vc-sq-fade ${away ? 'on' : ''}`}>
                {away && <span className="vc-away-label">away…</span>}
            </div>

            {/* controls top-right */}
            <span className="vc-sq-controls">
                <button
                    className={`vc-icon-btn ${maximized ? 'active' : ''}`}
                    title={maximized ? 'exit full grid' : 'fill the whole grid'}
                    onClick={() => onToggleMax(sub.streamId)}
                >⛶</button>
                <button
                    className={`vc-icon-btn ${sub.muted ? 'off' : ''}`}
                    title={sub.muted ? 'unmute their audio' : 'mute their audio'}
                    onClick={() => onToggleMute(sub.streamId)}
                >{sub.muted ? '🔇' : '🔊'}</button>
                <button
                    className="vc-icon-btn"
                    title="stop watching"
                    onClick={() => onUnwatch(sub.streamId)}
                >👁</button>
            </span>

            {/* lifetime-stats ticker (scrolling marquee along the very bottom);
                falls back to just the name until stats arrive */}
            <div className="vc-ticker">
                <span className="vc-ticker-text">
                    {lifetime ? tickerText(sub.name, lifetime) : sub.name}
                </span>
            </div>
        </div>
    );
}

function SelfPip({ stream }: { stream: MediaStream }) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => { /* autoplay guard */ });
        }
    }, [stream]);
    return (
        <div className="vc-selfpip">
            <video ref={videoRef} className="vc-selfpip-video" muted playsInline />
            <span className="vc-selfpip-label">You</span>
        </div>
    );
}

export function VideoGrid({ localStream, activeSubs, streamStats, userStats, starred, attachCanvas, unsubscribe, toggleMute }: {
    localStream: MediaStream | null;
    activeSubs: ActiveSub[];
    streamStats: Record<number, StreamLive>;
    userStats: Record<number, LifetimeStats>;
    starred: Set<number>;
    attachCanvas: (sub: ActiveSub, canvas: HTMLCanvasElement) => void;
    unsubscribe: (streamId: number) => void;
    toggleMute: (streamId: number) => void;
}) {
    // exactly 4 slots; fill in order, rest are placeholders
    const slots = Array.from({ length: 4 }, (_, i) => activeSubs[i] ?? null);
    const [maxId, setMaxId] = useState<number | null>(null);
    // effective: only honor the maximized id while that stream is still watched
    const maximizedId = maxId != null && activeSubs.some((a) => a.streamId === maxId) ? maxId : null;
    const toggleMax = (streamId: number) => setMaxId((cur) => (cur === streamId ? null : streamId));
    return (
        <div className="vc-video">
            <div className={`vc-grid4 ${maximizedId != null ? 'vc-grid-max' : ''}`}>
                {slots.map((sub, i) =>
                    sub
                        ? <RemoteSquare
                            key={sub.streamId} sub={sub} live={streamStats[sub.streamId]} lifetime={userStats[sub.streamId]}
                            starred={starred.has(sub.streamId)}
                            maximized={maximizedId === sub.streamId}
                            hidden={maximizedId != null && maximizedId !== sub.streamId}
                            attach={attachCanvas} onUnwatch={unsubscribe} onToggleMute={toggleMute} onToggleMax={toggleMax} />
                        : <div key={`empty-${i}`} className={`vc-sq vc-sq-empty ${maximizedId != null ? 'vc-sq-hidden' : ''}`}><span>empty</span></div>,
                )}
            </div>
            {localStream && (localStream.getVideoTracks().length > 0
                ? <SelfPip stream={localStream} />
                : <div className="vc-selfmic" title="you are sharing audio only">🎙 You — mic on</div>)}
        </div>
    );
}
